import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { CheckCircle2, ChevronRight, Loader2, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { cellDeepLink, listAnswersToCheck, runMyDigest, type AnswerToCheck } from '../utils/discoveryGridApi';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';

/**
 * "Answers to check" (DISCOVERY_GRID_REDESIGN_SPEC §4.11): the consultant's
 * level-3 cells across their companies, newest first, each a deep link to the
 * cell. Opening the cell marks it checked.
 */

interface AnswersToCheckPanelProps {
  accessToken: string;
  /** Already-loaded answers (the Queue loads them once for its count); when given, the panel does not fetch. */
  preloaded?: { items: AnswerToCheck[]; total: number };
}

export function AnswersToCheckPanel({ accessToken, preloaded }: AnswersToCheckPanelProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<AnswerToCheck[]>(preloaded?.items ?? []);
  const [total, setTotal] = useState(preloaded?.total ?? 0);
  const [loading, setLoading] = useState(!preloaded);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (preloaded) { setItems(preloaded.items); setTotal(preloaded.total); setLoading(false); return; }
    let cancelled = false;
    listAnswersToCheck(accessToken, 8)
      .then((r) => { if (!cancelled) { setItems(r.items); setTotal(r.total); } })
      .catch((e) => console.error('[AnswersToCheck] load failed:', e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [accessToken, preloaded]);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <Card className="mb-6 border-sky-200 bg-sky-50/40">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="size-4 text-sky-700" />{total} answer{total === 1 ? '' : 's'} to check</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs text-gray-600"
          disabled={sending}
          onClick={async () => {
            try { setSending(true); const r = await runMyDigest(accessToken); toast.success(`Digest sent: ${r.inApp} in-app, ${r.emails} email`); }
            catch (e: any) { toast.error(e?.message || 'Could not send the digest'); }
            finally { setSending(false); }
          }}
          title="Send yourself the digest now, ignoring the hourly and daily limits"
        >
          {sending ? <Loader2 className="size-3.5 mr-1.5 animate-spin" /> : <Mail className="size-3.5 mr-1.5" />}Email me the list
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-y divide-sky-100">
          {items.map((it) => (
            <li key={`${it.companyId}:${it.cellId}`}>
              <button
                type="button"
                onClick={() => navigate(cellDeepLink(it.companyId, it.cellId))}
                className="w-full text-left py-2 flex items-center gap-3 hover:bg-white/70 rounded px-1 group"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-gray-900 truncate">{it.text}</div>
                  <div className="text-[11px] text-gray-500 truncate">{it.companyName} · {formatCategoryLabel(it.category)} · {it.blockName}{it.answeredBy ? ` · ${it.answeredBy}` : ''}{it.preview ? ` · “${it.preview}”` : ''}</div>
                </div>
                <ChevronRight className="size-4 text-gray-400 group-hover:text-gray-700 shrink-0" />
              </button>
            </li>
          ))}
        </ul>
        {total > items.length && <div className="text-[11px] text-gray-500 mt-2">{total - items.length} more on the client grids.</div>}
      </CardContent>
    </Card>
  );
}
